import { getDocumentTitle } from '@/helpers/printing';
import { autosizeCb } from '@/helpers/reportingDashboard/printReporting';
import { useAppDispatch } from '@/store/hooks';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import ReportCard from '../ReportCard';
import ImpressionsByTypeGrid from './Grid';

const ImpressionByType = ({
  impressionTypes = [],
  getReport,
  viewType = '',
  viewTypes = [],
  onViewTypeChange,
  getReportLoading,
  mapScales = [],
  showViewSwitch = false,
  reportDispatch,
  requestSaver,
  disabled = false,
  currentType,
  setCurrentType,
  paginationModel = {
    pageSize: 10,
    page: 0,
  },
  setPaginationModel,
  printPdfMode = false,
  checkedForPrintList = [],
  handlePrintPdfModeToggle,
  handleRadioToggle,
  allowPrintAll = false,
}) => {
  const dispatch = useAppDispatch();

  const printContainerRef = useRef(null);
  const onTypeChange = value => {
    setCurrentType(value);
    setPaginationModel(prev => ({
      ...prev,
      page: 0,
    }));
  };

  const gridData = useMemo(() => getReport(currentType), [currentType, getReport]);
  const loadingGridData = useMemo(() => getReportLoading(currentType), [currentType, getReportLoading]);

  const handlePrintTable = useReactToPrint({
    documentTitle: getDocumentTitle('Impressions By Type'),
    content: () => printContainerRef.current,
    onBeforeGetContent: autosizeCb,
  });

  const refreshTable = useCallback(() => {
    if (!reportDispatch || !requestSaver) {
      return;
    }

    const entry = reportDispatch?.[currentType];
    if (!entry) return;

    const { action, reportRequest } = entry;
    dispatch(action(requestSaver.requestSaverSetCancellation(reportRequest)));
  }, [dispatch, currentType, reportDispatch, requestSaver]);

  return (
    <div className='grid grid-cols-4 col-span-4 print:gap-[10px] relative' ref={printContainerRef}>
      <ReportCard className={classNames('col-span-4', { 'print:hidden': printPdfMode || allowPrintAll })}>
        <ImpressionsByTypeGrid
          checkedForPrintList={checkedForPrintList}
          columns={gridData.columns}
          currentType={currentType}
          currentTypeLabel={currentType}
          data={loadingGridData ? [] : gridData.data}
          handleRadioToggle={handleRadioToggle}
          handlePrintPdfModeToggle={handlePrintPdfModeToggle}
          impressionTypes={impressionTypes}
          loading={loadingGridData}
          mapScales={mapScales}
          onTypeChange={onTypeChange}
          onViewTypeChange={onViewTypeChange}
          printPdfMode={printPdfMode}
          showViewSwitch={showViewSwitch}
          viewType={viewType}
          viewTypes={viewTypes}
          printContainerRef={printContainerRef}
          handlePrintTable={handlePrintTable}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          onGridRefresh={refreshTable}
          disabled={disabled}
        />
      </ReportCard>
      {checkedForPrintList
        .filter(type => !getReportLoading(type))
        .map(type => {
          const printGridData = getReport(type);
          return (
            <ReportCard key={type} className='col-span-4 absolute left-0 right-0 -z-10 print:static print:z-0'>
              <ImpressionsByTypeGrid
                columns={printGridData.columns}
                data={printGridData.data}
                viewType={viewType}
                currentTypeLabel={type}
                paginationModel={paginationModel}
                setPaginationModel={setPaginationModel}
                onGridRefresh={refreshTable}
                disabled={disabled}
              />
            </ReportCard>
          );
        })}
    </div>
  );
};

ImpressionByType.propTypes = {
  getReport: PropTypes.func,
  impressionTypes: PropTypes.array,
  viewType: PropTypes.string,
  viewTypes: PropTypes.array,
  onViewTypeChange: PropTypes.func,
  getReportLoading: PropTypes.func,
  mapScales: PropTypes.array,
  showViewSwitch: PropTypes.bool,
  disabled: PropTypes.bool,
  currentType: PropTypes.string,
  setCurrentType: PropTypes.func,
  paginationModel: PropTypes.object,
  setPaginationModel: PropTypes.func,
  printPdfMode: PropTypes.bool,
  checkedForPrintList: PropTypes.array,
  handlePrintPdfModeToggle: PropTypes.func,
  handleRadioToggle: PropTypes.func,
  allowPrintAll: PropTypes.bool,
};

export default ImpressionByType;
